import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime, fcfa } from "@/lib/format";
import { EmptyState, Loader, PageTitle, StatusBadge } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KpiCards } from "@/components/console/Kpis";

export function ConfirmDialog({ title, text, needReason, danger, onConfirm, onClose, label = "Confirmer" }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await onConfirm(reason);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="confirm-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {text && <p className="text-sm text-mr-text2">{text}</p>}
        {needReason && <textarea data-testid="confirm-reason" className="mr-input min-h-[80px]" placeholder="Motif (obligatoire)" value={reason} onChange={(e) => setReason(e.target.value)} />}
        <div className="flex justify-end gap-2">
          <button className="mr-btn-ghost" onClick={onClose} data-testid="confirm-cancel">Annuler</button>
          <button className={danger ? "mr-btn-danger" : "mr-btn-primary"} disabled={busy || (needReason && reason.trim().length < 3)} onClick={go} data-testid="confirm-ok">{busy ? "…" : label}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Detail = ({ d }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">{Object.entries(d || {}).filter(([k, v]) => !["participant_ids"].includes(k) && v !== "" && v !== null && typeof v !== "object").map(([k, v]) => (
    <div key={k} className="contents"><dt className="text-mr-muted">{k}</dt><dd>{typeof v === "number" && v > 1000 ? fcfa(v) : String(v)}</dd></div>))}
    {d?.participant_ids && <div className="contents"><dt className="text-mr-muted">participants</dt><dd>{d.participant_ids.length}</dd></div>}</dl>
);

export default function Validations() {
  const [d, setD] = useState(null);
  const [ask, setAsk] = useState(null);
  const load = () => api.get("/admin/validations").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  const decide = (it, approve) => setAsk({ it, approve });
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Validations" title="À valider" />
      <KpiCards section="validations" />
      {!d ? <Loader /> : d.items.length === 0 ? <EmptyState title="Rien à valider" text="Les opérations, groupes et événements soumis par les membres apparaîtront ici." testId="validations-empty" /> : (
        <div className="space-y-3">{d.items.map((it) => (
          <div key={`${it.kind}-${it.id}`} className="mr-card p-4 space-y-2" data-testid="validation-item">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><StatusBadge status="en_attente" label={it.kind_label} tone="info" /> <span className="font-medium ml-2">{it.title}</span>
                <p className="text-xs text-mr-muted mt-1">Soumis par {it.user_name || "—"} · {fdatetime(it.created_at)}</p></div>
              <div className="flex gap-2">
                <button data-testid="validation-approve" className="mr-btn-primary min-h-[38px]" onClick={() => decide(it, true)}><Check size={15} /> Approuver</button>
                <button data-testid="validation-reject" className="mr-btn-danger min-h-[38px]" onClick={() => decide(it, false)}><X size={15} /> Refuser</button>
              </div>
            </div>
            {it.summary && <p className="text-sm text-mr-text2">{it.summary}</p>}
            <Detail d={it.detail} />
          </div>))}</div>
      )}
      {d?.history?.length > 0 && <div className="mt-8"><p className="mr-eyebrow mb-2">Décisions récentes</p>
        {d.history.map((h) => <p key={h.id} className="text-sm py-1 border-b border-mr-border" data-testid="validation-history">{h.title} · {h.status === "approuve" ? "approuvée" : `refusée (${h.reason})`} · {fdatetime(h.updated_at)}</p>)}</div>}
      {ask && <ConfirmDialog title={ask.approve ? `Approuver « ${ask.it.title} » ?` : `Refuser « ${ask.it.title} » ?`} needReason={!ask.approve} danger={!ask.approve}
        text={ask.approve ? "L'élément devient visible et le membre est notifié." : "Le membre recevra le motif du refus."} label={ask.approve ? "Approuver" : "Refuser"}
        onClose={() => setAsk(null)} onConfirm={async (reason) => {
          await api.post(`/admin/validations/${ask.it.kind}/${ask.it.id}`, { approve: ask.approve, reason });
          toast.success(ask.approve ? "Demande approuvée" : "Demande refusée");
          load();
        }} />}
    </div>
  );
}
